/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useNavigate, useParams } from 'react-router-dom';
import { useMemo, useRef, useState } from 'react';

import copy from 'copy-to-clipboard';
import { I18n } from '@coze-arch/i18n';
import { Toast } from '@coze-arch/coze-design';
import { type DynamicParams } from '@coze-arch/bot-typings/teamspace';
import { isApiError } from '@coze-arch/bot-http';
import { buildCozeChatUrl } from '@coze-agent-ide/abr-chat-frame';

import { usePublishScopeConfig } from '../../hooks/use-publish-scope-config';
import { usePublishAppMetadata } from '../../hooks/use-publish-app-metadata';
import {
  PERMISSION_SPECIFIC_ORG,
  SCOPE_SPECIFIC_ORG_EMPTY_MSG,
} from '../../constants/permission-scope';
import { publishApp } from '../../api/published-apps';
import { PublishSuccessPanel } from './publish-success';
import {
  type PublishStep,
  PublishStepsHeader,
  stepToIndex,
} from './publish-steps';
import { PublishFormPanel } from './publish-form';
import { DeptTreeModal, type DeptTreeModalRef } from './dept-tree-modal';

import styles from './index.module.less';

export const AgentAppPublishPage = () => {
  const navigate = useNavigate();
  const params = useParams<DynamicParams>();
  const { bot_id: botId, space_id: spaceId } = params;

  const resolvedBotId = botId;
  const resolvedSpaceId = spaceId;

  const {
    loading: metaLoading,
    name,
    description,
    iconUrl,
    llmLabel,
    ontologyLabel,
  } = usePublishAppMetadata(resolvedBotId, resolvedSpaceId);

  const [versionNote, setVersionNote] = useState('');
  const {
    scopeValue,
    setScopeValue,
    loading: scopeLoading,
  } = usePublishScopeConfig(resolvedBotId);
  const deptModalRef = useRef<DeptTreeModalRef>(null);
  const [step, setStep] = useState<PublishStep>('form');
  const [accessUrl, setAccessUrl] = useState('');

  const chatUrl = useMemo(() => {
    if (!resolvedBotId || !resolvedSpaceId) {
      return '';
    }
    return buildCozeChatUrl({
      botId: resolvedBotId,
      spaceId: resolvedSpaceId,
      scene: 'release',
      entry: 'standalone',
    });
  }, [resolvedBotId, resolvedSpaceId]);

  const handleBack = () => {
    if (resolvedSpaceId && resolvedBotId) {
      navigate(`/space/${resolvedSpaceId}/bot/${resolvedBotId}`);
      return;
    }
    navigate(-1);
  };

  const handlePublish = async () => {
    if (!resolvedBotId || !resolvedSpaceId) {
      return;
    }
    const scopeType = String(scopeValue.scopeType || '0');
    if (
      scopeType === PERMISSION_SPECIFIC_ORG &&
      scopeValue.appointData.length === 0
    ) {
      Toast.warning(SCOPE_SPECIFIC_ORG_EMPTY_MSG);
      return;
    }
    setStep('publishing');
    try {
      await publishApp({
        bot_id: resolvedBotId,
        space_id: resolvedSpaceId,
        description: versionNote.trim() || undefined,
        scope_type: scopeType,
        scope_dept_ids:
          scopeType === PERMISSION_SPECIFIC_ORG
            ? scopeValue.appointData
            : undefined,
      });
      setAccessUrl(chatUrl);
      setStep('success');
    } catch (error) {
      setStep('form');
      const msg = isApiError(error)
        ? error.message
        : I18n.t('abr_app_publish_failed');
      Toast.error(msg);
    }
  };

  const handleCopy = () => {
    if (accessUrl && copy(accessUrl)) {
      Toast.success(I18n.t('abr_app_copy_success'));
    }
  };

  return (
    <div className={styles.page}>
      <PublishStepsHeader stepIndex={stepToIndex(step)} onBack={handleBack} />
      <div className={styles.content}>
        <div className={styles.column}>
          {step === 'success' ? (
            <PublishSuccessPanel
              accessUrl={accessUrl}
              onCopy={handleCopy}
              onGoStore={() => navigate('/apps')}
              onBack={handleBack}
            />
          ) : (
            <>
              <PublishFormPanel
                loading={metaLoading || scopeLoading}
                name={name}
                description={description}
                iconUrl={iconUrl}
                botId={resolvedBotId}
                llmLabel={llmLabel}
                ontologyLabel={ontologyLabel}
                versionNote={versionNote}
                scopeValue={scopeValue}
                step={step}
                onVersionNoteChange={setVersionNote}
                onScopeChange={setScopeValue}
                onOpenDept={() =>
                  deptModalRef.current?.open({ values: scopeValue.appointData })
                }
                onBack={handleBack}
                onPublish={handlePublish}
              />
              <DeptTreeModal
                ref={deptModalRef}
                onConfirm={(orgs, appointData) => {
                  setScopeValue(prev => ({ ...prev, orgs, appointData }));
                }}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
